import React, { useEffect, useState } from "react";
import styled from "styled-components";
import fowardWhite from "../../assets/fowardWhite.png";
import editIcon from "../../assets/edit.png";
import apiService from "../../services/api";
import { usePopup } from "../common/Popup";

const Container = styled.div`
  max-width: 700px;
  margin-left: 50px;
  padding: 10px;
  margin-top: 10px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const Titulo = styled.h2`
  font-size: 18px;
  color: #FF9F1F;
  font-weight: 600;
`;

const Secao = styled.div`
  margin-bottom: 18px;
`;

const SecaoDireita = styled(Secao)`
  text-align: right;
`;

const Label = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #002E3B;
`;

const Valor = styled.div`
  font-size: 14px;
  color: #4a4a4a;
  margin-top: 4px;
`;

const Linha = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 40px;
`;

const BotaoEditar = styled.button`
  background: none;
  border: none;
  color: #FF9F1F;
  cursor: pointer;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 4px;
  font-size: 14px;
  padding: 4px;
`;

const BotaoConfirmar = styled.button`
  background-color: #6AB8A8;
  color: white;
  font-size: 16px;
  font-weight: bold;
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  box-shadow: 0px 5px 3px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 40px;
  float: right;
`;

const BotaoVoltar = styled.button`
  background-color: #999;
  color: white;
  font-size: 16px;
  font-weight: bold;
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  box-shadow: 0px 5px 3px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 40px;
  margin-right: 10px;
  float: right;
  
  &:hover {
    background-color: #777;
  }
`;

const BotoesContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 40px;
  clear: both;
`;

const InputCampo = styled.input`
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 8px;
  outline: none;
  color: #002E3B;
`;

const SmallInfo = styled.span`
  font-size: 12px;
  color: #666;
  margin-right: 8px;
`;

const ConfirmacaoEstabelecimentoSaude = ({
  data,
  onEdit,
  onConfirm,
  enableInlineEdit = false,
  estabelecimentoId,
  onUpdated,
  onClose,
}) => {
  const popup = usePopup();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(data || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setFormData(data || {});
    }
  }, [data, isEditing]);

  const handleChangeField = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditClick = () => {
    if (enableInlineEdit && estabelecimentoId) {
      if (isEditing) {
        setIsEditing(false);
        setFormData(data || {});
      } else {
        setIsEditing(true);
      }
    } else if (onEdit) {
      onEdit();
    }
  };

  const handleSave = async () => {
    if (!enableInlineEdit || !estabelecimentoId) {
      if (onConfirm) onConfirm();
      return;
    }

    try {
      setSaving(true);

      const payload = {
        nome: formData.nomeEstabelecimento ?? data?.nomeEstabelecimento,
        nomeDiretor: formData.nomeDiretor ?? "",
        cargoDiretor: formData.cargoDiretor ?? "",
        nomeDiretorTecnico: formData.nomeDiretorTecnico ?? "",
        cargoDiretorTecnico: formData.cargoDiretorTecnico ?? "",
        nomeResponsavelTecnico:
          formData.nomeResponsavelEstagio ??
          formData.nomeResponsavelTecnico ??
          "",
        cargoResponsavelTecnico:
          formData.cargoResponsavelEstagio ??
          formData.cargoResponsavelTecnico ??
          "",
      };

      const atualizado = await apiService.updateEstabelecimentoSaude(
        estabelecimentoId,
        payload
      );

      setIsEditing(false);
      setFormData((prev) => ({
        ...prev,
        nomeEstabelecimento: atualizado.nome,
        nomeDiretor: atualizado.nomeDiretor,
        cargoDiretor: atualizado.cargoDiretor,
        nomeDiretorTecnico: atualizado.nomeDiretorTecnico,
        cargoDiretorTecnico: atualizado.cargoDiretorTecnico,
        nomeResponsavelEstagio: atualizado.nomeResponsavelTecnico,
        cargoResponsavelEstagio: atualizado.cargoResponsavelTecnico,
      }));

      if (onUpdated) {
        onUpdated(atualizado);
      }
      await popup.alert({
        icon: "success",
        message: "Dados do estabelecimento atualizados com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao atualizar estabelecimento:", error);
      await popup.alert({
        icon: "warning",
        title: "Erro ao atualizar",
        message:
          error.message ||
          "Verifique os dados e tente novamente.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmClick = () => {
    if (enableInlineEdit && estabelecimentoId) {
      handleSave();
    } else if (onConfirm) {
      onConfirm();
    }
  };

  const currentData = isEditing ? formData : data;

  return (
    <Container>
      <HeaderRow>
        <div>
          <Titulo>Confira os dados</Titulo>
          {enableInlineEdit && (
            <SmallInfo>
              {isEditing
                ? "Edite os campos desejados e clique em Salvar."
                : "Clique em editar para atualizar os dados."}
            </SmallInfo>
          )}
        </div>
        <BotaoEditar onClick={handleEditClick}>
          <img src={editIcon} alt="Editar" width="24" />
          {enableInlineEdit && estabelecimentoId
            ? isEditing
              ? "cancelar"
              : "editar"
            : "editar"}
        </BotaoEditar>
      </HeaderRow>

      <Secao>
        <Label>Nome do estabelecimento de saúde</Label>
        <Valor>
          {isEditing ? (
            <InputCampo
              value={currentData?.nomeEstabelecimento || ""}
              onChange={handleChangeField("nomeEstabelecimento")}
            />
          ) : (
            currentData?.nomeEstabelecimento
          )}
        </Valor>
      </Secao>

      <Linha>
        <Secao>
          <Label>Nome do diretor</Label>
          <Valor>
            {isEditing ? (
              <InputCampo
                value={currentData?.nomeDiretor || ""}
                onChange={handleChangeField("nomeDiretor")}
              />
            ) : (
              currentData?.nomeDiretor
            )}
          </Valor>
        </Secao>
        <SecaoDireita>
          <Label>Cargo</Label>
          <Valor>
            {isEditing ? (
              <InputCampo
                value={currentData?.cargoDiretor || ""}
                onChange={handleChangeField("cargoDiretor")}
              />
            ) : (
              currentData?.cargoDiretor
            )}
          </Valor>
        </SecaoDireita>
      </Linha>

      <Linha>
        <Secao>
          <Label>Nome do diretor técnico</Label>
          <Valor>
            {isEditing ? (
              <InputCampo
                value={currentData?.nomeDiretorTecnico || ""}
                onChange={handleChangeField("nomeDiretorTecnico")}
              />
            ) : (
              currentData?.nomeDiretorTecnico
            )}
          </Valor>
        </Secao>
        <SecaoDireita>
          <Label>Cargo</Label>
          <Valor>
            {isEditing ? (
              <InputCampo
                value={currentData?.cargoDiretorTecnico || ""}
                onChange={handleChangeField("cargoDiretorTecnico")}
              />
            ) : (
              currentData?.cargoDiretorTecnico
            )}
          </Valor>
        </SecaoDireita>
      </Linha>

      <Linha>
        <Secao>
          <Label>Nome do responsável pelo setor de estágio</Label>
          <Valor>
            {isEditing ? (
              <InputCampo
                value={currentData?.nomeResponsavelEstagio || ""}
                onChange={handleChangeField("nomeResponsavelEstagio")}
              />
            ) : (
              currentData?.nomeResponsavelEstagio
            )}
          </Valor>
        </Secao>
        <SecaoDireita>
          <Label>Cargo</Label>
          <Valor>
            {isEditing ? (
              <InputCampo
                value={currentData?.cargoResponsavelEstagio || ""}
                onChange={handleChangeField("cargoResponsavelEstagio")}
              />
            ) : (
              currentData?.cargoResponsavelEstagio
            )}
          </Valor>
        </SecaoDireita>
      </Linha>

      {enableInlineEdit && estabelecimentoId && !isEditing && onClose ? (
        <BotoesContainer>
          <BotaoVoltar onClick={onClose}>
            Voltar
          </BotaoVoltar>
        </BotoesContainer>
      ) : (
        <BotaoConfirmar onClick={handleConfirmClick} disabled={saving}>
          {enableInlineEdit && estabelecimentoId
            ? saving
              ? "Salvando..."
              : "Salvar"
            : "Próximo"}
          <img src={fowardWhite} alt="Confirmar" width="20" />
        </BotaoConfirmar>
      )}
    </Container>
  );
};

export default ConfirmacaoEstabelecimentoSaude;
